import { Article } from '@wix/answers-api';
import { Box, Text } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { PageSparkles, ChevronDownSmall, ChevronUpSmall } from '@wix/wix-ui-icons-common';
import classNames from 'classnames';
import React, { useState, useEffect } from 'react';

import { PanelAction } from '../../../types';
import { useArticlePageBi } from '../../../utils/article-page-bi';
import { useStore } from '../../../zustand/stores';

import css from './index.module.scss';

interface ArticleSummaryProps {
  summary: string;
  article: Article;
  summaryRef: React.RefObject<HTMLDivElement>;
}

export const ArticleSummary: React.FC<ArticleSummaryProps> = ({
  summary,
  article,
  summaryRef,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const { articleSummaryTrack } = useArticlePageBi(article);
  const { user } = useStore((state) => state.userSlice);

  useEffect(() => {
    setDisplayedText('');
    setIsTyping(true);
  }, [article]);

  useEffect(() => {
    if (summary && isTyping) {
      let currentIndex = 0;
      const typingInterval = setInterval(() => {
        if (isExpanded) {
          clearInterval(typingInterval);
          setIsTyping(false);
          setDisplayedText(summary);
        }
        if (currentIndex < summary.length) {
          setDisplayedText(summary.slice(0, currentIndex + 1));
          currentIndex++;
        } else {
          setIsTyping(false);
          clearInterval(typingInterval);
        }
      }, 15);

      return () => clearInterval(typingInterval);
    }
  }, [summary, isTyping, isExpanded]);

  const toggleExpanded = async () => {
    const newExpandedState = !isExpanded;
    const action = newExpandedState ? PanelAction.EXPAND : PanelAction.COLLAPSE;

    setDisplayedText(summary);
    setIsExpanded(newExpandedState);
    await articleSummaryTrack(action);
  };

  return (
    <div
      className={classNames(css.articleSummaryContainer, {
        [css.expanded]: isExpanded,
        [css.userIsLoggedIn]: !user,
      })}
      onClick={toggleExpanded}
      data-typing-complete={!isTyping}
    >
      <Box className={css.wrapper} direction="vertical" ref={summaryRef}>
        <Box className={css.titleContainer} verticalAlign="middle">
          <PageSparkles />
          <Text size="small" className={css.title}>
            {t('article-page.copilot.summary.title')}
          </Text>
        </Box>
        <Box
          className={classNames(css.summaryContainer, {
            [css.expanded]: isExpanded,
          })}
        >
          <Text size="small">{isTyping ? displayedText : summary}</Text>
        </Box>
        <Box className={css.expandButton} align="center">
          {isExpanded ? <ChevronUpSmall /> : <ChevronDownSmall />}
        </Box>
      </Box>
    </div>
  );
};
